import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { ReactComponent as ArrowIcon } from '../assets/images/icons/ic-arrow.svg'
import { ReactComponent as CheckIcon } from '../assets/images/icons/ic-check.svg'
import { ReactComponent as CopyIcon } from '../assets/images/icons/ic-copy.svg'
import api from '../api'
import Button from '../components/Button'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import Select from '../components/Select'
import TextInput from '../components/TextInput'
import typo from '../typography.module.css'
import styles from './DevelopersDocumentation.module.css'

const nodeExample = `const crypto = require('crypto')

const body = JSON.stringify({ event: 'lead.created', data: { id: 123 } })
const secret = process.env.VELV_SECRET_KEY
const signature = crypto
  .createHmac('sha256', secret)
  .update(body)
  .digest('hex')

const timestamp = Math.floor(Date.now() / 1000).toString()
const nonce = crypto.randomUUID()

await fetch('https://api.startingfinance.com/integrations/v1', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Client-ID': 'my-company-client-id',
    'X-Key-ID': 'sf_pk_xxxxx',
    'X-Timestamp': timestamp,
    'X-Nonce': nonce,
    'X-Signature': signature,
  },
  body,
})`

const pythonExample = `import hashlib
import hmac
import json
import time
import uuid
import requests

payload = {"event": "lead.created", "data": {"id": 123}}
body = json.dumps(payload, separators=(",", ":"))
secret = 'YOUR_SECRET_KEY'

signature = hmac.new(
    secret.encode('utf-8'),
    body.encode('utf-8'),
    hashlib.sha256
).hexdigest()

timestamp = str(int(time.time()))
nonce = str(uuid.uuid4())

response = requests.post(
    'https://api.startingfinance.com/integrations/v1',
    data=body,
    headers={
        'Content-Type': 'application/json',
        'X-Client-ID': 'my-company-client-id',
        'X-Key-ID': 'sf_pk_xxxxx',
        'X-Timestamp': timestamp,
        'X-Nonce': nonce,
        'X-Signature': signature,
    }
)
print(response.status_code, response.text)`

const curlExample = `BODY='{"event":"lead.created","data":{"id":123}}'
TS=$(date +%s)
NONCE=$(uuidgen)
SECRET='YOUR_SECRET_KEY'
SIG=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SECRET" -hex | sed 's/^.* //')

curl -X POST 'https://api.startingfinance.com/integrations/v1' \\
  -H 'Content-Type: application/json' \\
  -H "X-Client-ID: my-company-client-id" \\
  -H "X-Key-ID: sf_pk_xxxxx" \\
  -H "X-Timestamp: $TS" \\
  -H "X-Nonce: $NONCE" \\
  -H "X-Signature: $SIG" \\
  --data "$BODY"`

const healthcheckResponseExample = {
  status: 'ok',
  service: 'integrations',
  server_timestamp_unix: 1775603381,
  server_timestamp_utc: '2026-04-07T23:09:41.781042+00:00',
  client_id: 'my-company-client-id',
  key_id: 'sf_pk_xxxxx',
  company_id: 2,
  rate_limit: {
    key: {
      max_requests: 60,
      window_seconds: 60,
    },
    ip: {
      max_requests: 120,
      window_seconds: 60,
    },
  },
}

const leadsRequestExample = {
  first_name: 'Mario',
  last_name: 'Rossi',
  email: 'mario.rossi@example.com',
  phone: '+393331112233',
  country: 'Italia',
  city: 'Milano',
  age: 29,
  gender: 'M',
  product_id: 101,
  metadata: {
    source: 'landing-page',
    campaign: 'spring-2026',
  },
}

const leadsRequestFields = [
  { name: 'first_name', type: 'string', required: true, description: 'Nome del lead.' },
  { name: 'last_name', type: 'string', required: true, description: 'Cognome del lead.' },
  { name: 'email', type: 'string', required: true, description: 'Email del lead.' },
  { name: 'phone', type: 'string', required: true, description: 'Numero di telefono in formato internazionale.' },
  { name: 'country', type: 'string', required: true, description: 'Paese di provenienza del lead.' },
  { name: 'city', type: 'string', required: true, description: 'Città del lead.' },
  { name: 'age', type: 'integer', required: true, description: 'Età del lead.' },
  { name: 'gender', type: 'enum', required: true, description: 'Genere del lead. Valori accettati: male, female, other.' },
  { name: 'product_id', type: 'integer', required: true, description: 'ID prodotto associato al lead.' },
  { name: 'metadata', type: 'object', required: false, description: 'Dati aggiuntivi opzionali in formato chiave/valore.' },
]

const integrationErrors = [
  {
    code: 'MissingClientId',
    detail: 'Missing X-Client-ID header',
    status: 401,
    backendCode: 37,
    backendMessage: 'Integration Missing Client ID',
    explanation: 'La richiesta non contiene il client id dell’azienda, quindi il backend non può risolvere il tenant corretto.',
    solutions: [
      'Inserisci sempre l’header X-Client-ID.',
      'Verifica che il valore usato corrisponda al client_id mostrato nella sezione Developers.',
    ],
  },
  {
    code: 'MissingKeyId',
    detail: 'Missing X-Key-ID header',
    status: 401,
    backendCode: 38,
    backendMessage: 'Integration Missing Key ID',
    explanation: 'Manca il riferimento alla chiave pubblica da usare per validare la firma.',
    solutions: [
      'Aggiungi l’header X-Key-ID con la chiave pubblica (sf_pk_...).',
      'Controlla che la chiave non sia revocata.',
    ],
  },
  {
    code: 'MissingSignature',
    detail: 'Missing X-Signature header',
    status: 401,
    backendCode: 39,
    backendMessage: 'Integration Missing Signature',
    explanation: 'La richiesta non contiene la firma HMAC, quindi non è autenticabile.',
    solutions: [
      'Calcola la firma HMAC SHA256 del body raw.',
      'Invia la firma nell’header X-Signature.',
    ],
  },
  {
    code: 'MissingNonce',
    detail: 'Missing X-Nonce header',
    status: 401,
    backendCode: 40,
    backendMessage: 'Integration Missing Nonce',
    explanation: 'Il nonce serve per prevenire replay attack; senza nonce la richiesta viene rifiutata.',
    solutions: [
      'Genera un nonce univoco per ogni richiesta (es. UUID v4).',
      'Invialo nell’header X-Nonce.',
    ],
  },
  {
    code: 'MissingTimestamp',
    detail: 'Missing X-Timestamp header',
    status: 401,
    backendCode: 41,
    backendMessage: 'Integration Missing Timestamp',
    explanation: 'Manca il timestamp necessario per validare la finestra temporale della firma.',
    solutions: [
      'Aggiungi l’header X-Timestamp in secondi Unix.',
      'Sincronizza l’orologio del server che invia le richieste.',
    ],
  },
  {
    code: 'InvalidTimestamp',
    detail: 'Invalid X-Timestamp header',
    status: 401,
    backendCode: 42,
    backendMessage: 'Integration Invalid Timestamp',
    explanation: 'Il timestamp non è in formato valido o non è parsabile come intero.',
    solutions: [
      'Invia il timestamp come numero intero in secondi.',
      'Evita formati data testuali o millisecondi non previsti.',
    ],
  },
  {
    code: 'ExpiredSignature',
    detail: 'Expired integration signature',
    status: 401,
    backendCode: 43,
    backendMessage: 'Integration Expired Signature',
    explanation: 'La richiesta è fuori dalla finestra temporale accettata dalla piattaforma.',
    solutions: [
      'Rigenera timestamp e firma immediatamente prima della chiamata.',
      'Controlla drift orario tra il tuo server e UTC.',
    ],
  },
  {
    code: 'InvalidIntegrationKey',
    detail: 'Invalid integration key',
    status: 401,
    backendCode: 44,
    backendMessage: 'Integration Invalid Key',
    explanation: 'La combinazione tra X-Client-ID e X-Key-ID non è valida o la key è stata revocata.',
    solutions: [
      'Verifica che X-Client-ID e X-Key-ID appartengano alla stessa azienda.',
      'Se la key è revocata, genera una nuova coppia.',
    ],
  },
  {
    code: 'InvalidSignature',
    detail: 'Invalid signature',
    status: 401,
    backendCode: 45,
    backendMessage: 'Integration Invalid Signature',
    explanation: 'La firma ricevuta non coincide con quella ricalcolata dal backend.',
    solutions: [
      'Firma il body raw esattamente come inviato (stessa serializzazione).',
      'Usa la chiave segreta corretta e algoritmo HMAC SHA256.',
      'Verifica encoding UTF-8 e assenza di modifiche al payload dopo la firma.',
    ],
  },
  {
    code: 'NonceAlreadyUsed',
    detail: 'Nonce already used',
    status: 401,
    backendCode: 46,
    backendMessage: 'Integration Nonce Already Used',
    explanation: 'Il nonce è già stato usato in precedenza e la richiesta è bloccata per sicurezza.',
    solutions: [
      'Genera un nonce nuovo per ogni chiamata.',
      'Non riutilizzare nonce in retry: crea una nuova richiesta firmata.',
    ],
  },
  {
    code: 'RateLimitExceeded',
    detail: 'Rate limit exceeded / IP rate limit exceeded',
    status: 429,
    backendCode: 47,
    backendMessage: 'Integration Rate Limit Exceeded',
    explanation: 'Il numero di richieste ha superato i limiti consentiti nella finestra temporale configurata (sia per chiave che per IP).',
    solutions: [
      'Riduci il numero di richieste al secondo con queue o throttling lato client.',
      'Implementa retry con exponential backoff quando ricevi HTTP 429.',
      'Distribuisci il traffico evitando burst ravvicinati.',
    ],
  },
]

const DevelopersDocumentation = () => {
  const navigate = useNavigate()
  const sections = useMemo(() => ([
    { id: 'overview', label: 'Overview' },
    { id: 'authentication', label: 'Autenticazione' },
    { id: 'verify-signature', label: 'Verifica Firma' },
  ]), [])

  const apiEndpoints = useMemo(() => ([
    { id: 'get-root', method: 'GET', path: '/' },
    { id: 'post-leads', method: 'POST', path: '/leads' },
  ]), [])

  const examples = useMemo(() => ([
    { id: 'python', label: 'Python', language: 'python', code: pythonExample },
    { id: 'node', label: 'Node.js', language: 'javascript', code: nodeExample },
    { id: 'curl', label: 'cURL', language: 'bash', code: curlExample },
  ]), [])

  const [selectedSection, setSelectedSection] = useState('overview')
  const [selectedApiEndpoint, setSelectedApiEndpoint] = useState(apiEndpoints[0]?.id || null)
  const [selectedExample, setSelectedExample] = useState(examples[0])
  const [selectedErrorCode, setSelectedErrorCode] = useState(integrationErrors[0].code)
  const [copiedUrlKey, setCopiedUrlKey] = useState(null)
  const [verifyKeyPublic, setVerifyKeyPublic] = useState('')
  const [verifyBody, setVerifyBody] = useState('')
  const [verifySignature, setVerifySignature] = useState('')
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [verifyResult, setVerifyResult] = useState(null)
  const [activeKeys, setActiveKeys] = useState([])
  const [activeKeysLoading, setActiveKeysLoading] = useState(false)

  const selectedError = useMemo(() => (
    integrationErrors.find((error) => error.code === selectedErrorCode) || integrationErrors[0]
  ), [selectedErrorCode])

  const verifyKeyOptions = useMemo(() => (
    activeKeys.map((key) => ({
      id: key.id,
      value: key.public,
      name: key.public,
    }))
  ), [activeKeys])

  const selectedVerifyKeyOption = useMemo(() => (
    verifyKeyOptions.find((option) => option.value === verifyKeyPublic) || null
  ), [verifyKeyOptions, verifyKeyPublic])

  useEffect(() => {
    if (!copiedUrlKey) {
      return
    }

    const timeout = setTimeout(() => {
      setCopiedUrlKey(null)
    }, 1500)

    return () => clearTimeout(timeout)
  }, [copiedUrlKey])

  useEffect(() => {
    if (selectedSection !== 'verify-signature') {
      return
    }

    if (activeKeys.length > 0 || activeKeysLoading) {
      return
    }

    const loadActiveKeys = async () => {
      setActiveKeysLoading(true)
      try {
        const response = await api.get('/company/developers/keys')
        const keys = (response?.keys || []).filter((key) => key.active)
        setActiveKeys(keys)
        if (!verifyKeyPublic && keys.length > 0) {
          setVerifyKeyPublic(keys[0].public)
        }
      } catch (e) {
        console.error(e)
        setActiveKeys([])
      }
      setActiveKeysLoading(false)
    }

    loadActiveKeys()
  }, [selectedSection, activeKeys, activeKeysLoading, verifyKeyPublic])

  const onCopyUrl = async (url, key) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedUrlKey(key)
    } catch (e) {
      console.error(e)
    }
  }

  const onVerifySignature = async () => {
    if (!verifyKeyPublic.trim() || !verifySignature.trim()) {
      setVerifyResult({
        valid: false,
        message: 'Compila almeno Key Public e Signature.',
      })
      return
    }

    setVerifyLoading(true)
    setVerifyResult(null)
    try {
      const response = await api.post('/company/developers/keys/verify-signature', {
        key_public: verifyKeyPublic.trim(),
        body: verifyBody,
        signature: verifySignature.trim(),
      })
      setVerifyResult(response)
    } catch (e) {
      console.error(e)
      setVerifyResult({
        valid: false,
        message: e?.response?.data?.detail || 'Errore durante la verifica della firma.',
      })
    }
    setVerifyLoading(false)
  }

  return (
    <HeaderFooterLayout hideHeader>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Button
              appearance='subtle'
              onClick={() => navigate(-1)}
              style={{ alignSelf: 'flex-start' }}
            >
              <ArrowIcon style={{ width: '11px', height: '11px' }} />
              Torna indietro
            </Button>
            <div className={typo.title}>Documentazione</div>

            <div className={styles.docsLayout}>
              <aside className={styles.sidebar}>
                <div className={styles.sidebarTitle}>OVERVIEW</div>
                <div className={styles.sidebarList}>
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      type='button'
                      className={`${styles.sidebarItem} ${selectedSection === section.id ? styles.sidebarItemActive : ''}`}
                      onClick={() => setSelectedSection(section.id)}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>

                <div className={styles.sidebarTitle}>API</div>
                <div className={styles.sidebarList}>
                  {apiEndpoints.map((endpoint) => {
                    const isActive = selectedSection === 'api' && selectedApiEndpoint === endpoint.id
                    const methodTagClass = endpoint.method === 'POST' ? styles.httpMethodTagPost : styles.httpMethodTagGet
                    return (
                      <button
                        key={endpoint.id}
                        type='button'
                        className={`${styles.sidebarItem} ${styles.sidebarEndpointItem} ${isActive ? styles.sidebarItemActive : ''}`}
                        onClick={() => {
                          setSelectedSection('api')
                          setSelectedApiEndpoint(endpoint.id)
                        }}
                      >
                        <span className={methodTagClass}>{endpoint.method}</span>
                        <span>{endpoint.path}</span>
                      </button>
                    )
                  })}
                </div>
              </aside>

              <div className={styles.contentArea}>
                {selectedSection === 'overview' && (
                  <div className={styles.card}>
                    <div className={styles.cardTitle}>Overview</div>
                    <div className={styles.description}>
                      Per inviare richieste valide verso le API integrations devi includere sempre tutti gli elementi richiesti.
                      Ogni campo ha un ruolo specifico nella validazione di autenticità, integrità e anti-replay.
                      Per validare in modo rapido la corretta implementazione della firma,
                      puoi usare anche il tool interno nella sezione{' '}
                      <span className={styles.inlineSectionLink} onClick={() => setSelectedSection('verify-signature')}>
                        Verifica Firma
                      </span>.
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.infoTitle}>Base URL</div>
                      <div className={styles.description}>https://api.startingfinance.com/integrations/v1</div>
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.infoTitle}>Elementi Richiesti</div>
                      <ul className={styles.list}>
                        <li><b>Client ID (X-Client-ID)</b>: identifica l'azienda chiamante.</li>
                        <li><b>Key ID (X-Key-ID)</b>: identifica la chiave pubblica da validare.</li>
                        <li><b>Timestamp (X-Timestamp)</b>: timestamp unix in secondi per validità temporale della richiesta.</li>
                        <li><b>Nonce (X-Nonce)</b>: valore univoco per ogni chiamata per prevenire replay attack.</li>
                        <li><b>Body raw</b>: payload esatto da inviare e da firmare senza alterazioni.</li>
                        <li>
                          <b>Secret key</b>: chiave segreta usata solo lato server per il calcolo HMAC.
                          <div className={styles.securityWarning}>
                            Non salvare mai la chiave privata lato client e non mapparla nel codice sorgente.
                            Deve rimanere solo lato server, in variabili d'ambiente o sistemi di secret management.
                          </div>
                        </li>
                        <li><b>Signature (X-Signature)</b>: hash HMAC SHA256 del body raw generato con la secret key.</li>
                      </ul>
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.infoTitle}>Rate Limit</div>
                      <ul className={styles.list}>
                        <li><b>Limite per key</b>: default 60 richieste ogni 60 secondi (client_id + key_id).</li>
                        <li><b>Limite per IP</b>: default 120 richieste ogni 60 secondi.</li>
                        <li><b>Sforamento</b>: ritorna HTTP 429 con codice backend 47.</li>
                      </ul>

                      <div className={styles.suggestionWarning}>
                        <b>Suggerimento:</b> implementa retry con backoff esponenziale e jitter quando ricevi HTTP 429.
                      </div>
                    </div>

                  </div>
                )}

                {selectedSection === 'authentication' && (
                  <div className={styles.card}>
                    <div className={styles.cardTitle}>Autenticazione</div>
                    <div className={styles.description}>
                      Ogni chiamata alle API deve essere firmata con HMAC SHA256 del body raw usando la chiave segreta.
                      La richiesta è accettata solo se firma, timestamp, nonce, client id e chiave pubblica risultano validi.
                    </div>
                    <ul className={styles.list}>
                      <li><b>X-Client-ID</b>: L'identificativo dell'azienda (client_id).</li>
                      <li><b>X-Key-ID</b>: La tua chiave pubblica.</li>
                      <li><b>X-Timestamp</b>: timestamp unix in secondi.</li>
                      <li><b>X-Nonce</b>: valore univoco per richiesta (UUID v4).</li>
                      <li><b>X-Signature</b>: HMAC SHA256 del body raw usando la chiave segreta.</li>
                    </ul>

                    <ol className={styles.list}>
                      <li>Genera il body JSON e serializzalo.</li>
                      <li>Calcola la firma HMAC SHA256 con la chiave segreta.</li>
                      <li>Invia body + headers richiesti.</li>
                    </ol>

                    <div className={styles.examplesTitle}>Esempi</div>
                    <div className={styles.tabs}>
                      {examples.map((example) => (
                        <button
                          key={example.id}
                          type='button'
                          className={`${styles.tab} ${selectedExample.id === example.id ? styles.tabActive : ''}`}
                          onClick={() => setSelectedExample(example)}
                        >
                          {example.label}
                        </button>
                      ))}
                    </div>

                    <div className={styles.codeContainer}>
                      <SyntaxHighlighter
                        language={selectedExample.language}
                        style={vscDarkPlus}
                        showLineNumbers
                        wrapLongLines
                        customStyle={{
                          margin: 0,
                          borderRadius: '10px',
                          fontSize: '0.8rem',
                          lineHeight: 1.45,
                          padding: '0.95rem',
                        }}
                      >
                        {selectedExample.code}
                      </SyntaxHighlighter>
                    </div>

                    <div className={styles.examplesTitle}>Errori Possibili</div>
                    <div className={styles.errorsSplitLayout}>
                      <div className={styles.errorsListPanel}>
                        {integrationErrors.map((error) => (
                          <button
                            key={error.code}
                            type='button'
                            className={`${styles.errorListItem} ${selectedError.code === error.code ? styles.errorListItemActive : ''}`}
                            onClick={() => setSelectedErrorCode(error.code)}
                          >
                            <div className={styles.errorCode}>{error.code}</div>
                            <div className={styles.errorDetail}>{error.detail}</div>
                          </button>
                        ))}
                      </div>

                      <div className={styles.errorDetailPanel}>
                        <div className={styles.errorCardTop}>
                          <div className={styles.errorCode}>{selectedError.code}</div>
                          <div className={styles.errorStatus}>HTTP {selectedError.status}</div>
                        </div>

                        <div className={styles.errorExplanation}><b>Spiegazione:</b> {selectedError.explanation}</div>
                        <div className={styles.errorSolutionsTitle}>Possibili soluzioni:</div>
                        <ul className={styles.errorSolutionsList}>
                          {selectedError.solutions.map((solution) => (
                            <li key={solution}>{solution}</li>
                          ))}
                        </ul>

                        <div className={styles.errorSolutionsTitle}>JSON Body:</div>
                        <pre className={styles.errorJson}>
                          {JSON.stringify({
                            code: selectedError.backendCode,
                            message: selectedError.backendMessage,
                            detail: selectedError.detail,
                          }, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {selectedSection === 'verify-signature' && (
                  <div className={styles.card}>
                    <div className={styles.cardTitle}>Verifica Firma</div>
                    <div className={styles.description}>
                      Tool interno per verificare rapidamente se la firma HMAC inviata corrisponde al body raw per una key pubblica specifica.
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.verifyFormGrid}>
                        <label className={styles.verifyLabel}>
                          <span>Key Public</span>
                          <Select
                            selected={selectedVerifyKeyOption}
                            options={verifyKeyOptions}
                            placeholder={activeKeysLoading ? 'Caricamento chiavi...' : 'Nessuna chiave attiva disponibile'}
                            onSelect={(option) => setVerifyKeyPublic(option?.value || '')}
                            loading={activeKeysLoading}
                            disabled={!activeKeysLoading && verifyKeyOptions.length === 0}
                          />
                        </label>

                        <label className={styles.verifyLabel}>
                          <span>Signature</span>
                          <TextInput
                            type='text'
                            value={verifySignature}
                            onKeyUp={(value) => setVerifySignature(value || '')}
                            placeholder='sha256=... oppure hash hex'
                          />
                        </label>

                        <label className={styles.verifyLabel}>
                          <span>Body Raw</span>
                          <TextInput
                            type='textarea'
                            value={verifyBody}
                            onKeyUp={(value) => setVerifyBody(value || '')}
                            resizable='vertical'
                            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" }}
                            placeholder='Inserisci il body raw esatto usato per la firma (anche vuoto per GET).'
                          />
                        </label>
                      </div>

                      <div className={styles.verifyActionRow}>
                        <Button appearance='subtle' onClick={onVerifySignature} disabled={verifyLoading}>
                          {verifyLoading ? 'Verifica in corso...' : 'Verifica Firma'}
                        </Button>
                      </div>

                      {verifyResult && (
                        <div className={`${styles.verifyResultBox} ${verifyResult.valid ? styles.verifyResultSuccess : styles.verifyResultError}`}>
                          <b>{verifyResult.valid ? 'Firma valida' : 'Firma non valida'}</b>
                          <span>{verifyResult.message}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedSection === 'api' && selectedApiEndpoint === 'get-root' && (
                  <div className={styles.card}>
                    <div className={styles.apiCardTitleRow}>
                      <span className={styles.httpMethodTagGet}>GET</span>
                      <span className={styles.apiCardPath}>/</span>
                    </div>
                    <div className={styles.description}>
                      Endpoint di health check autenticato per verificare disponibilità servizio,
                      timestamp server e configurazione rate limit associata alla chiave.
                      Questa chiamata può essere usata in fase di test per verificare la corretta
                      implementazione del sistema di autenticazione (headers, firma, timestamp e nonce).
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.infoTitle}>URL</div>
                      <div className={styles.apiUrlRow}>
                        <div className={styles.apiUrlText}>https://api.startingfinance.com/integrations/v1</div>
                        <Button
                          appearance='text'
                          onClick={() => onCopyUrl('https://api.startingfinance.com/integrations/v1', 'get-root-url')}
                          style={{ padding: 0 }}
                          aria-label='Copia URL GET /'
                        >
                          {copiedUrlKey === 'get-root-url'
                            ? <CheckIcon style={{ width: '16px', height: '16px' }} />
                            : <CopyIcon style={{ width: '16px', height: '16px' }} />}
                        </Button>
                      </div>
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.infoTitle}>Body</div>
                      <div className={styles.description}>null</div>
                    </div>

                    <div className={styles.examplesTitle}>Esempio Risposta</div>
                    <div className={styles.codeContainer}>
                      <SyntaxHighlighter
                        language='json'
                        style={vscDarkPlus}
                        showLineNumbers
                        wrapLongLines
                        customStyle={{
                          margin: 0,
                          borderRadius: '10px',
                          fontSize: '0.8rem',
                          lineHeight: 1.45,
                          padding: '0.95rem',
                        }}
                      >
                        {JSON.stringify(healthcheckResponseExample, null, 2)}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                )}

                {selectedSection === 'api' && selectedApiEndpoint === 'post-leads' && (
                  <div className={styles.card}>
                    <div className={styles.apiCardTitleRow}>
                      <span className={styles.httpMethodTagPost}>POST</span>
                      <span className={styles.apiCardPath}>/leads</span>
                    </div>
                    <div className={styles.description}>
                      Endpoint autenticato per inviare un lead e validare il payload lato server.
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.infoTitle}>URL</div>
                      <div className={styles.apiUrlRow}>
                        <div className={styles.apiUrlText}>https://api.startingfinance.com/integrations/v1/leads</div>
                        <Button
                          appearance='text'
                          onClick={() => onCopyUrl('https://api.startingfinance.com/integrations/v1/leads', 'post-leads-url')}
                          style={{ padding: 0 }}
                          aria-label='Copia URL POST /leads'
                        >
                          {copiedUrlKey === 'post-leads-url'
                            ? <CheckIcon style={{ width: '16px', height: '16px' }} />
                            : <CopyIcon style={{ width: '16px', height: '16px' }} />}
                        </Button>
                      </div>
                    </div>

                    <div className={styles.infoBlock}>
                      <div className={styles.apiBodyHeaderRow}>
                        <div className={styles.infoTitle}>Body</div>
                        <div className={styles.apiBodyContentType}>application/json</div>
                      </div>
                      <ul className={styles.apiFieldsList}>
                        {leadsRequestFields.map((field) => (
                          <li key={field.name} className={styles.apiFieldItem}>
                            <div className={styles.apiFieldTopRow}>
                              <div className={styles.apiFieldName}>
                                {field.name} <span className={styles.apiFieldType}>({field.type})</span>
                              </div>
                              {field.required && <span className={styles.requiredTag}>Obbligatorio</span>}
                            </div>
                            <div className={styles.apiFieldDescription}>
                              {field.description}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={styles.examplesTitle}>Esempio Richiesta</div>
                    <div className={styles.codeContainer}>
                      <SyntaxHighlighter
                        language='json'
                        style={vscDarkPlus}
                        showLineNumbers
                        wrapLongLines
                        customStyle={{
                          margin: 0,
                          borderRadius: '10px',
                          fontSize: '0.8rem',
                          lineHeight: 1.45,
                          padding: '0.95rem',
                        }}
                      >
                        {JSON.stringify(leadsRequestExample, null, 2)}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  )
}

export default DevelopersDocumentation
